import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Star, ShoppingCart, Truck, Shield, ChevronLeft, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import ProductReviews from "@/components/ProductReviews";
import ProductQA from "@/components/ProductQA";

export default function ProductDetail() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { format, addToCart, country, settings } = useApp();
  const [p, setP] = useState(null);
  const [color, setColor] = useState("");
  const [size, setSize] = useState("");
  const [qty, setQty] = useState(1);

  useEffect(() => {
    api.get(`/products/${slug}`).then((r) => {
      setP(r.data);
      setColor(r.data.colors?.[0] || "");
      setSize(r.data.sizes?.[0] || "");
    }).catch(() => nav("/produtos"));
  }, [slug, nav]);

  if (!p) return <div className="mx-auto max-w-7xl px-4 py-20 text-center">A carregar...</div>;

  const shipDays = settings?.shipping_rules?.find((r) => r.country === country)?.days
    || settings?.shipping_rules?.find((r) => r.country === "*")?.days || "7-14";

  const hasSale = p.sale_price_eur && p.sale_price_eur < p.price_eur;

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <Link to="/produtos" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="back-to-products">
        <ChevronLeft className="w-4 h-4" /> Voltar
      </Link>

      <div className="grid lg:grid-cols-2 gap-12">
        <div className="rounded-3xl overflow-hidden border border-border bg-secondary">
          <img src={p.image} alt={p.name} className="w-full h-auto object-cover" />
        </div>

        <div className="space-y-5">
          <div className="text-xs uppercase tracking-widest text-primary">{p.category_slug}</div>
          <h1 className="text-3xl sm:text-4xl font-display font-bold">{p.name}</h1>
          <div className="flex items-center gap-2 text-sm">
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map(i => <Star key={i} className={`w-4 h-4 ${i <= Math.round(p.rating) ? "fill-yellow-400 text-yellow-400" : "text-muted"}`} />)}
            </div>
            <span className="font-medium">{p.rating}</span>
            <span className="text-muted-foreground">({p.reviews_count} avaliações)</span>
          </div>

          <div className="flex items-baseline gap-3">
            <span className="text-4xl font-display font-bold" data-testid="product-price">
              {format(p.sale_price_eur ?? p.price_eur)}
            </span>
            {hasSale && <span className="text-lg line-through text-muted-foreground">{format(p.price_eur)}</span>}
          </div>

          <p className="text-muted-foreground leading-relaxed">{p.description}</p>

          <div className="flex items-center gap-2 text-sm">
            {p.stock > 0 ? (
              <><CheckCircle2 className="w-4 h-4 text-green-500" /> <span>Em stock ({p.stock} disponíveis)</span></>
            ) : (
              <><XCircle className="w-4 h-4 text-destructive" /> <span>Esgotado</span></>
            )}
          </div>

          {p.colors?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Cor</div>
              <div className="flex gap-2 flex-wrap">
                {p.colors.map((c) => (
                  <button key={c} onClick={() => setColor(c)} data-testid={`color-${c}`}
                    className={`px-4 py-1.5 rounded-full text-sm border transition ${color === c ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}>{c}</button>
                ))}
              </div>
            </div>
          )}

          {p.sizes?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Modelo/Tamanho</div>
              <div className="flex gap-2 flex-wrap">
                {p.sizes.map((s) => (
                  <button key={s} onClick={() => setSize(s)} data-testid={`size-${s}`}
                    className={`px-4 py-1.5 rounded-full text-sm border transition ${size === s ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"}`}>{s}</button>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center gap-3 pt-2">
            <div className="flex items-center border border-border rounded-full">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="px-3 py-2" data-testid="qty-minus">-</button>
              <span className="px-4 font-mono" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(qty + 1)} className="px-3 py-2" data-testid="qty-plus">+</button>
            </div>
            <Button size="lg" className="rounded-full btn-primary flex-1" onClick={() => { addToCart(p, { color, size, quantity: qty }); toast.success("Adicionado ao carrinho"); }} disabled={p.stock === 0} data-testid="pdp-add-cart">
              <ShoppingCart className="w-4 h-4 mr-2" /> Adicionar ao carrinho
            </Button>
            <Button size="lg" variant="outline" className="rounded-full" onClick={() => { addToCart(p, { color, size, quantity: qty }); nav("/checkout"); }} disabled={p.stock === 0} data-testid="pdp-buy-now">
              Comprar agora
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-4">
            <div className="rounded-xl border border-border p-4 flex items-start gap-3">
              <Truck className="w-5 h-5 text-primary shrink-0" />
              <div>
                <div className="font-semibold text-sm">Entrega em {shipDays} dias</div>
                <div className="text-xs text-muted-foreground">Para o teu país</div>
              </div>
            </div>
            <div className="rounded-xl border border-border p-4 flex items-start gap-3">
              <Shield className="w-5 h-5 text-primary shrink-0" />
              <div>
                <div className="font-semibold text-sm">Pagamento seguro</div>
                <div className="text-xs text-muted-foreground">PCI-DSS</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <ProductReviews slug={slug} />
      <ProductQA slug={slug} />
    </div>
  );
}
